import { Check } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import type { EscritorioMembro } from '@/api/client';
import type { AlvoCompartilhamento } from '@/api/compartilhamento';
import type { Equipe } from '@/api/equipes';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PAPEIS_NO_PLURAL = { SOCIO: 'Sócios', ASSOCIADO: 'Associados', ESTAGIARIO: 'Estagiários' } as const;

export const chaveDoAlvo = (a: AlvoCompartilhamento) => `${a.tipo}:${a.valor ?? ''}`;

/**
 * Os destinos escolhidos, como conjunto. Com "Todo o escritório" marcado, só ele vale: os
 * papéis, equipes e pessoas escondidos não vão ao servidor.
 */
export function useDestinos(iniciais: AlvoCompartilhamento[]) {
  const [alvos, setAlvos] = useState<Map<string, AlvoCompartilhamento>>(
    () => new Map(iniciais.map((a) => [chaveDoAlvo(a), a])),
  );
  const alternar = (a: AlvoCompartilhamento) =>
    setAlvos((atual) => {
      const novo = new Map(atual);
      if (novo.has(chaveDoAlvo(a))) novo.delete(chaveDoAlvo(a));
      else novo.set(chaveDoAlvo(a), a);
      return novo;
    });
  const todoEscritorio = alvos.has('ESCRITORIO:');
  const escolhidos: AlvoCompartilhamento[] = todoEscritorio ? [{ tipo: 'ESCRITORIO' }] : [...alvos.values()];
  // Compara o que vai ao servidor: um destino escondido por "Todo o escritório" não conta.
  const mudou = (de: AlvoCompartilhamento[]) => {
    const atuais = new Set(escolhidos.map(chaveDoAlvo));
    return atuais.size !== de.length || de.some((a) => !atuais.has(chaveDoAlvo(a)));
  };
  return { alvos, alternar, escolhidos, mudou };
}

export function Opcao({ ativa, onClick, children }: { ativa: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-pressed={ativa}
      onClick={onClick}
      className={cn('h-11 gap-1.5 sm:h-8', ativa && 'border-primary bg-primary/5 text-primary hover:bg-primary/10')}
    >
      {ativa && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </Button>
  );
}

interface DestinosPickerProps {
  /** Para o ``aria-labelledby`` do grupo (único na tela). */
  id: string;
  titulo: ReactNode;
  alvos: Map<string, AlvoCompartilhamento>;
  onAlternar: (a: AlvoCompartilhamento) => void;
  equipes: Equipe[];
  /** Os colegas (sem quem escolhe). */
  colegas: EscritorioMembro[];
}

/** "Com quem": todo o escritório, papéis, equipes ou pessoas (A4). */
export function DestinosPicker({ id, titulo, alvos, onAlternar, equipes, colegas }: DestinosPickerProps) {
  const todoEscritorio = alvos.has('ESCRITORIO:');
  return (
    <div className="space-y-2" role="group" aria-labelledby={id}>
      <p id={id} className="text-sm font-medium">
        {titulo}
      </p>
      <div className="flex flex-wrap gap-2">
        <Opcao ativa={todoEscritorio} onClick={() => onAlternar({ tipo: 'ESCRITORIO' })}>
          Todo o escritório
        </Opcao>
      </div>
      {!todoEscritorio && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {(['SOCIO', 'ASSOCIADO', 'ESTAGIARIO'] as const).map((papel) => (
              <Opcao
                key={papel}
                ativa={alvos.has(`PAPEL:${papel}`)}
                onClick={() => onAlternar({ tipo: 'PAPEL', valor: papel })}
              >
                {PAPEIS_NO_PLURAL[papel]}
              </Opcao>
            ))}
          </div>
          {equipes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {equipes.map((e) => (
                <Opcao
                  key={e.id}
                  ativa={alvos.has(`EQUIPE:${e.id}`)}
                  onClick={() => onAlternar({ tipo: 'EQUIPE', valor: e.id })}
                >
                  Equipe {e.nome}
                </Opcao>
              ))}
            </div>
          )}
          {colegas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {colegas.map((m) => (
                <Opcao
                  key={m.usuario_id}
                  ativa={alvos.has(`USUARIO:${m.usuario_id}`)}
                  onClick={() => onAlternar({ tipo: 'USUARIO', valor: m.usuario_id })}
                >
                  {m.usuario_nome}
                </Opcao>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Os destinos em texto, para dizer com quem o item está ("Todo o escritório", "Sócios e Dra. X"). */
export function descreverDestinos(
  alvos: AlvoCompartilhamento[],
  equipes: Equipe[],
  membros: EscritorioMembro[],
): string {
  const nomes = alvos.map((a) => {
    if (a.tipo === 'ESCRITORIO') return 'todo o escritório';
    if (a.tipo === 'PAPEL') return PAPEIS_NO_PLURAL[a.valor as keyof typeof PAPEIS_NO_PLURAL] ?? a.valor ?? '';
    if (a.tipo === 'EQUIPE') return `equipe ${equipes.find((e) => e.id === a.valor)?.nome ?? ''}`.trim();
    return membros.find((m) => m.usuario_id === a.valor)?.usuario_nome ?? 'um colega';
  });
  if (nomes.length <= 1) return nomes[0] ?? '';
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}
